// Alert rules as the console reads them (plan step 3.5; Console D's #/alerts): each preset's
// parameters with the API's bounds (packages/accounts alerts/presets.ts), a rule's one-line
// summary and its condition as code, what it measures against its threshold (APS-303's
// last_value), and an event in a few words. Shared by Alerts, a rule's page, Settings ›
// Destinations, Agents and tokens, and Home.
import type { AlertDestination, AlertEvent, AlertPreset, AlertRule } from '../api/types'
import type { StatusKey } from '../components/d'
import type { IconName } from '../components/icons'
import type { MessageId, Translator } from '../i18n'

export const PRESETS: AlertPreset[] = [
  'crash_free_below',
  'new_issue',
  'issue_regressed',
  'issue_spike',
  'endpoint_failure_above',
  'store_crash_rate',
  'store_anr_rate',
]

export interface Field {
  key: string
  label: MessageId
  default?: number
  min: number
  max: number
  step: number
}

/** Google Play's thresholds (D62, alerts/presets.ts storeFields): user-days over 28 days. */
const storeFields = (overall: number): Field[] => [
  {
    key: 'threshold_percent',
    label: 'settings.alerts.f.threshold',
    default: overall,
    min: 0.01,
    max: 100,
    step: 0.01,
  },
  {
    key: 'device_threshold_percent',
    label: 'settings.alerts.f.deviceThreshold',
    default: 8,
    min: 0,
    max: 100,
    step: 0.1,
  },
  { key: 'window_days', label: 'settings.alerts.f.days', default: 28, min: 1, max: 28, step: 1 },
  {
    key: 'min_users',
    label: 'settings.alerts.f.minUserDays',
    default: 500,
    min: 10,
    max: 100_000_000,
    step: 1,
  },
  {
    key: 'min_device_users',
    label: 'settings.alerts.f.minDeviceUserDays',
    default: 100,
    min: 10,
    max: 100_000_000,
    step: 1,
  },
]

/** Each preset's numeric parameters, with the API's bounds (alerts/presets.ts). */
export const FIELDS: Record<AlertPreset, Field[]> = {
  crash_free_below: [
    {
      key: 'threshold_percent',
      label: 'settings.alerts.f.threshold',
      default: 99.5,
      min: 50,
      max: 99.99,
      step: 0.01,
    },
    {
      key: 'window_hours',
      label: 'settings.alerts.f.hours',
      default: 24,
      min: 1,
      max: 168,
      step: 1,
    },
    {
      key: 'min_sessions',
      label: 'settings.alerts.f.minSessions',
      default: 100,
      min: 10,
      max: 10_000_000,
      step: 1,
    },
  ],
  new_issue: [
    {
      key: 'min_occurrences',
      label: 'settings.alerts.f.minOccurrences',
      default: 1,
      min: 1,
      max: 10_000,
      step: 1,
    },
    {
      key: 'min_users',
      label: 'settings.alerts.f.minUsers',
      default: 0,
      min: 0,
      max: 10_000_000,
      step: 1,
    },
  ],
  // One message per regression the reopen job records (D100): nothing to tune.
  issue_regressed: [],
  issue_spike: [
    { key: 'factor', label: 'settings.alerts.f.factor', default: 5, min: 2, max: 1000, step: 0.5 },
    {
      key: 'min_occurrences',
      label: 'settings.alerts.f.minOccurrences',
      default: 20,
      min: 5,
      max: 10_000_000,
      step: 1,
    },
  ],
  endpoint_failure_above: [
    {
      key: 'threshold_percent',
      label: 'settings.alerts.f.threshold',
      default: 5,
      min: 0.1,
      max: 100,
      step: 0.1,
    },
    { key: 'window_hours', label: 'settings.alerts.f.hours', default: 1, min: 1, max: 24, step: 1 },
    {
      key: 'min_requests',
      label: 'settings.alerts.f.minRequests',
      default: 100,
      min: 20,
      max: 100_000_000,
      step: 1,
    },
  ],
  store_crash_rate: storeFields(1.09),
  store_anr_rate: storeFields(0.47),
}

/** Presets that take an optional release. */
export const HAS_RELEASE: AlertPreset[] = ['crash_free_below', 'new_issue']
/** Presets on the whole project: the API takes no app for them. */
export const NO_APP: AlertPreset[] = ['issue_regressed']
/** What a GitHub or Linear destination takes (D44): a new crash group, a crash-free regression. */
export const HANDOFF_PRESETS: AlertPreset[] = ['new_issue', 'crash_free_below']

export const KIND_LABEL: Record<AlertDestination['kind'], MessageId> = {
  slack: 'settings.alerts.kind.slack',
  webhook: 'settings.alerts.kind.webhook',
  github: 'settings.alerts.kind.github',
  linear: 'settings.alerts.kind.linear',
  pagerduty: 'settings.alerts.kind.pagerduty',
  email: 'settings.alerts.kind.email',
}

/** A destination's icon: Slack's, GitHub's, the handoff's, an envelope, or a bolt. */
export function destinationIcon(d: Pick<AlertDestination, 'kind'>): IconName {
  switch (d.kind) {
    case 'slack':
      return 'slack'
    case 'github':
      return 'git'
    case 'linear':
      return 'agent'
    case 'email':
      return 'mail'
    default:
      return 'bolt'
  }
}

/** A PagerDuty Events API v2 integration key, as the API checks it. */
export const ROUTING_KEY = /^[A-Za-z0-9]{32}$/

export const PRESET_LABEL: Record<AlertPreset, MessageId> = {
  crash_free_below: 'settings.alerts.preset.crash_free_below',
  new_issue: 'settings.alerts.preset.new_issue',
  issue_regressed: 'settings.alerts.preset.issue_regressed',
  issue_spike: 'settings.alerts.preset.issue_spike',
  endpoint_failure_above: 'settings.alerts.preset.endpoint_failure_above',
  store_crash_rate: 'settings.alerts.preset.store_crash_rate',
  store_anr_rate: 'settings.alerts.preset.store_anr_rate',
}

export const num = (v: unknown) => (typeof v === 'number' ? v : Number(v ?? 0))
const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined)

/** One line saying what a rule watches. */
export function ruleSummary(
  rule: Pick<AlertRule, 'preset' | 'params'>,
  { t, number }: Pick<Translator, 't' | 'number'>,
): string {
  const p = rule.params
  // A threshold as the conditions write it: 99.50 %, 5.0 %, 1.09 %.
  const pct = (v: unknown) =>
    number(num(v), { minimumFractionDigits: num(v) >= 90 ? 2 : 1, maximumFractionDigits: 2 })
  const release = str(p.release)
  const base = (() => {
    switch (rule.preset) {
      case 'crash_free_below':
        return t('settings.alerts.summary.crash_free_below', {
          threshold: pct(p.threshold_percent),
          hours: num(p.window_hours),
        })
      case 'new_issue':
        return t('settings.alerts.summary.new_issue', { min: num(p.min_occurrences) })
      case 'issue_regressed':
        return t('settings.alerts.summary.issue_regressed')
      case 'issue_spike':
        return t('settings.alerts.summary.issue_spike', {
          factor: number(num(p.factor)),
          min: num(p.min_occurrences),
        })
      case 'endpoint_failure_above':
        return t('settings.alerts.summary.endpoint_failure_above', {
          threshold: pct(p.threshold_percent),
          hours: num(p.window_hours),
        })
      case 'store_crash_rate':
      case 'store_anr_rate': {
        const values = {
          threshold: pct(p.threshold_percent),
          device: pct(p.device_threshold_percent),
          days: num(p.window_days),
        }
        const crash = rule.preset === 'store_crash_rate'
        return num(p.device_threshold_percent) > 0
          ? t(
              crash
                ? 'settings.alerts.summary.store_crash_rate'
                : 'settings.alerts.summary.store_anr_rate',
              values,
            )
          : t(
              crash
                ? 'settings.alerts.summary.store_crash_rate_overall'
                : 'settings.alerts.summary.store_anr_rate_overall',
              values,
            )
      }
    }
  })()
  return release ? t('settings.alerts.summary.inRelease', { rule: base, release }) : base
}

/**
 * A rule's condition as code (the mock's `crash_free_sessions < 99.50% over 24h`): telemetry
 * names and numbers, never translated. Built from the rule's own parameters.
 */
export function ruleCondition(rule: Pick<AlertRule, 'preset' | 'params'>): string {
  const p = rule.params
  // A threshold as the mock writes it: 99.50 %, 5.0 % (the second decimal near 100 %).
  const pct = (v: unknown) => `${num(v).toFixed(num(v) >= 90 ? 2 : 1)} %`
  const release = str(p.release)
  const on = release ? ` on ${release}` : ''
  const endpoint =
    str(p.method) && str(p.host) ? ` on ${p.method} ${p.host}${str(p.template) ?? ''}` : ''
  switch (rule.preset) {
    case 'crash_free_below':
      return `crash_free_sessions < ${pct(p.threshold_percent)} over ${num(p.window_hours)}h, min ${num(p.min_sessions)} sessions${on}`
    case 'new_issue':
      return `new_issue.occurrences >= ${num(p.min_occurrences)}${num(p.min_users) > 0 ? `, users >= ${num(p.min_users)}` : ''}${on}`
    case 'issue_regressed':
      return 'issue.regressed: a resolved issue seen in a later release'
    case 'issue_spike':
      return `issue.occurrences_1h > ${num(p.factor)}x hourly_mean, min ${num(p.min_occurrences)}`
    case 'endpoint_failure_above':
      return `endpoint.failure_rate > ${pct(p.threshold_percent)} over ${num(p.window_hours)}h, min ${num(p.min_requests)} requests${endpoint}`
    case 'store_crash_rate':
    case 'store_anr_rate': {
      const name = rule.preset === 'store_crash_rate' ? 'play.crash_rate' : 'play.anr_rate'
      const device =
        num(p.device_threshold_percent) > 0
          ? ` or >= ${pct(p.device_threshold_percent)} on one device`
          : ''
      return `${name} >= ${pct(p.threshold_percent)}${device} over ${num(p.window_days)}d, min ${num(p.min_users)} user-days`
    }
  }
}

/** What a rule measures and where it fires, in the unit of `last_value`. */
export interface Measure {
  unit: 'ratio' | 'factor'
  threshold: number
  firesWhen: 'below' | 'above'
}

/** A rule's threshold in the unit the evaluator measures; none for the issue presets. */
export function measureOf(rule: Pick<AlertRule, 'preset' | 'params'>): Measure | undefined {
  const p = rule.params
  switch (rule.preset) {
    case 'crash_free_below':
      return { unit: 'ratio', threshold: num(p.threshold_percent) / 100, firesWhen: 'below' }
    case 'endpoint_failure_above':
    case 'store_crash_rate':
    case 'store_anr_rate':
      return { unit: 'ratio', threshold: num(p.threshold_percent) / 100, firesWhen: 'above' }
    case 'issue_spike':
      return { unit: 'factor', threshold: num(p.factor), firesWhen: 'above' }
    default:
      return undefined
  }
}

/** A measured value as the mock writes it: 99.38 %, 3.1 %, 3.2×. */
export function formatMeasure(
  i18n: Pick<Translator, 'number'>,
  value: number,
  unit: Measure['unit'],
): string {
  if (unit === 'factor') return `${i18n.number(value, { maximumFractionDigits: 1 })}×`
  const pct = value * 100
  // Near 100 % (crash-free) and under 1 % the second decimal is the one that moves.
  const digits = pct >= 90 || pct < 1 ? 2 : 1
  return `${i18n.number(pct, { minimumFractionDigits: digits, maximumFractionDigits: digits })} %`
}

/**
 * How far a rule's last value is from firing, in its unit (percentage points for a rate), and
 * that distance against the threshold, for ordering "closest to firing". Undefined without a
 * measure or a value.
 */
export function headroom(
  rule: Pick<AlertRule, 'preset' | 'params' | 'last_value'>,
): { value: number; share: number; measure: Measure } | undefined {
  const m = measureOf(rule)
  if (!m || rule.last_value === null || rule.last_value === undefined) return undefined
  const value =
    m.firesWhen === 'below' ? rule.last_value - m.threshold : m.threshold - rule.last_value
  // For crash-free the room is what is left of the allowed failure (1 − threshold).
  const scale = m.firesWhen === 'below' ? 1 - m.threshold : m.threshold
  return { value, share: scale > 0 ? value / scale : value, measure: m }
}

/** A headroom as the mock writes it: 1.9 pts for a rate, 1.8× for a spike. */
export function formatHeadroom(
  i18n: Pick<Translator, 'number'>,
  h: NonNullable<ReturnType<typeof headroom>>,
): string {
  return h.measure.unit === 'factor'
    ? `${i18n.number(h.value, { maximumFractionDigits: 1 })}×`
    : `${i18n.number(h.value * 100, { maximumFractionDigits: 2 })} pts`
}

/** A rule's state as D's status: off, muted until, firing or ok. */
export function ruleStatus(
  r: Pick<AlertRule, 'enabled' | 'muted_until' | 'state'>,
  now: number,
  { t, date }: Pick<Translator, 't' | 'date'>,
): { s: StatusKey; label: string; key: 'off' | 'muted' | 'firing' | 'ok' } {
  if (!r.enabled) return { s: 'mute', label: t('settings.alerts.state.off'), key: 'off' }
  if (r.muted_until && Date.parse(r.muted_until) > now)
    return {
      s: 'mute',
      label: t('settings.alerts.state.muted', {
        date: date(r.muted_until, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
      }),
      key: 'muted',
    }
  if (r.state === 'firing')
    return { s: 'fire', label: t('settings.alerts.state.firing'), key: 'firing' }
  return { s: 'on', label: t('settings.alerts.state.ok'), key: 'ok' }
}

/** What one event was, in a few words. */
export function eventSummary(
  e: AlertEvent,
  { t, number }: Pick<Translator, 't' | 'number'>,
): string {
  const d = e.detail
  switch (e.kind) {
    case 'test':
      return t('settings.alerts.event.test')
    case 'suppressed':
      return t('settings.alerts.event.suppressed', {
        window: d.window === 'day' ? 'day' : 'minute',
      })
    case 'new_issue':
      return t('settings.alerts.event.new_issue', { occurrences: num(d.occurrences) })
    case 'issue_regressed':
      return str(d.fixed_in)
        ? t('settings.alerts.event.issue_regressedFixed', { fixed: str(d.fixed_in) ?? '' })
        : t('settings.alerts.event.issue_regressed')
    case 'issue_resolved':
      return t('settings.alerts.event.issue_resolved', {
        status: d.status === 'ignored' ? 'ignored' : 'resolved',
      })
    case 'fired':
    case 'resolved': {
      const value =
        e.value === null
          ? '—'
          : typeof d.last_hour === 'number'
            ? `${number(Math.round(e.value))}×`
            : formatMeasure({ number }, e.value, 'ratio')
      return t(
        e.kind === 'fired' ? 'settings.alerts.event.fired' : 'settings.alerts.event.resolved',
        { value },
      )
    }
  }
}

/** A delivery as D's status: sent, queued or sending, failed, held back. */
export const DELIVERY: Record<AlertEvent['delivery'], { s: StatusKey; label: MessageId }> = {
  sent: { s: 'res', label: 'settings.alerts.delivery.sent' },
  pending: { s: 'on', label: 'settings.alerts.delivery.pending' },
  sending: { s: 'on', label: 'settings.alerts.delivery.sending' },
  failed: { s: 'fire', label: 'settings.alerts.delivery.failed' },
  suppressed: { s: 'mute', label: 'settings.alerts.delivery.suppressed' },
}

/** The issue an event is about, when it names one (a new issue, a spike, a regression). */
export const eventIssue = (e: AlertEvent): string | undefined => str(e.detail.fingerprint)
